import { API_BASE } from "../config/constants";
import { isFullOfflineModeEnabled } from "./fullOfflineMode";
import { offlinePriceListRequest } from "../offline/priceListStore";


export const isMultiplePricingEnabled = (profile) =>
  [true, 1, "1", "true", "yes"].includes(
    typeof profile?.multiple_price_lists_enabled === "string"
      ? profile.multiple_price_lists_enabled.toLowerCase()
      : profile?.multiple_price_lists_enabled
  );

const toSafePriceNumber = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  const raw = String(value).trim();
  if (!raw) return NaN;
  const direct = Number(raw);
  if (Number.isFinite(direct)) return direct;
  const cleaned = raw.replace(/[^0-9.-]/g, "");
  if (!cleaned) return NaN;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
};

/** Canonical tax-inclusive unit rate used by POS display and persistence. */
export const resolvePayableUnitPrice = (entity = {}) => {
  const candidates = [
    entity?.taxedprice,
    entity?.priceaftertax,
    entity?.price,
    entity?.price_after_tax,
    entity?.rate,
    entity?.unit_price,
    entity?.item_price,
  ];
  for (const candidate of candidates) {
    const numeric = toSafePriceNumber(candidate);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }

  const amount = toSafePriceNumber(
    entity?.amount ?? entity?.line_total ?? entity?.total
  );
  const quantity = toSafePriceNumber(entity?.quantity ?? entity?.qty);
  if (
    Number.isFinite(amount) &&
    amount > 0 &&
    Number.isFinite(quantity) &&
    quantity > 0
  ) {
    return amount / quantity;
  }

  return 0;
};

/**
 * Rebuild derived tax prices from the canonical entered price, rate and tax mode.
 * This also protects the POS from stale derived fields returned after a bulk update.
 */
export const normalizeProductTaxPricing = (entity = {}, splitTax = true) => {
  const price = toSafePriceNumber(entity?.price);
  const percent = toSafePriceNumber(
    entity?.gstpercetage ?? entity?.gstpercentage ?? entity?.gst_percent ?? entity?.gst
  );
  const enabled = [true, 1, "1", "true", "yes"].includes(
    typeof (entity?.gstenable ?? entity?.gst_enabled) === "string"
      ? String(entity?.gstenable ?? entity?.gst_enabled).toLowerCase()
      : entity?.gstenable ?? entity?.gst_enabled
  );
  const mode = String(entity?.withorwithoutgst ?? entity?.tax_type ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]/g, "");

  if (!Number.isFinite(price) || price < 0 || !enabled || !(percent > 0)) {
    if (!Number.isFinite(price) || price < 0) return entity;
    return {
      ...entity,
      priceaftertax: price,
      taxedprice: price,
      taxamount: 0,
      sgst: 0,
      cgst: 0,
    };
  }

  const withTaxIncluded = ["with", "withgst", "withtax"].includes(mode);
  const taxRate = percent / 100;
  const taxable = withTaxIncluded ? price / (1 + taxRate) : price;
  const gross = withTaxIncluded ? price : price * (1 + taxRate);
  const taxamount = Number((gross - taxable).toFixed(2));
  const cgst = splitTax ? Number((taxamount / 2).toFixed(2)) : 0;
  const sgst = splitTax ? Number((taxamount - cgst).toFixed(2)) : 0;

  return {
    ...entity,
    priceaftertax: Number(taxable.toFixed(2)),
    taxedprice: Number(gross.toFixed(2)),
    taxamount,
    cgst,
    sgst,
  };
};

export const priceListRequest = async (path, payload) => {
  if (isFullOfflineModeEnabled()) {
    return offlinePriceListRequest(path, payload);
  }
  const response = await fetch(`${API_BASE}/price-lists/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.success === false) {
    const validationMessage = json?.errors
      ? Object.values(json.errors).flat().find(Boolean)
      : null;
    throw new Error(validationMessage || json?.message || "Price list request failed");
  }
  return json;
};

export const scalePriceFields = (entity, alternatePrice) => {
  const target = Number(alternatePrice);
  if (!Number.isFinite(target) || target < 0) return entity;
  const regularDisplay = Number(
    entity?.taxedprice ?? entity?.priceaftertax ?? entity?.price ?? 0
  );
  if (!(regularDisplay > 0)) {
    return { ...entity, price: target, priceaftertax: target, taxedprice: target, taxamount: 0 };
  }
  const ratio = target / regularDisplay;
  const scaled = (value, fallback = 0) =>
    Number((Number(value ?? fallback) * ratio).toFixed(2));
  return {
    ...entity,
    price: scaled(entity?.price, regularDisplay),
    priceaftertax: scaled(entity?.priceaftertax, regularDisplay),
    taxedprice: target,
    taxamount: scaled(entity?.taxamount, 0),
    sgst: scaled(entity?.sgst, 0),
    cgst: scaled(entity?.cgst, 0),
  };
};

export const restoreSavedLineTax = (savedLine = {}, referenceLine = {}) => {
  const savedPrice = Number(savedLine?.price ?? savedLine?.priceaftertax ?? 0);
  const savedBase = Number(savedLine?.priceaftertax ?? savedLine?.price ?? 0);
  const referenceBase = Number(referenceLine?.priceaftertax ?? referenceLine?.price ?? 0);
  const referenceTax = Number(referenceLine?.taxamount ?? 0);
  const explicitTaxedPrice = Number(savedLine?.taxedprice);
  const hasExplicitTaxedPrice =
    savedLine?.taxedprice !== null &&
    savedLine?.taxedprice !== undefined &&
    savedLine?.taxedprice !== "" &&
    Number.isFinite(explicitTaxedPrice);
  const scale = referenceBase > 0 ? savedBase / referenceBase : 0;
  const taxamount = Number(
    (hasExplicitTaxedPrice
      ? Math.max(0, explicitTaxedPrice - savedBase)
      : referenceTax * scale
    ).toFixed(2)
  );
  return {
    ...referenceLine,
    ...savedLine,
    price: savedPrice,
    priceaftertax: savedBase,
    taxedprice: hasExplicitTaxedPrice
      ? explicitTaxedPrice
      : Number((savedBase + taxamount).toFixed(2)),
    taxamount,
    sgst: Number((Number(referenceLine?.sgst ?? 0) * scale).toFixed(2)),
    cgst: Number((Number(referenceLine?.cgst ?? 0) * scale).toFixed(2)),
  };
};
